import { useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import { Button, IconButton, Typography, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { DateTime } from "luxon";
import {
  Datagrid,
  FunctionField,
  List,
  RecordContextProvider,
  ReferenceField,
  ReferenceInput,
  SearchInput,
  SelectInput,
  useGetList,
  useGetMany,
  useListContext,
  type RowClickFunction,
} from "react-admin";
import { AddAppointmentDialog } from "../../dialogs/AddAppointmentDialog/AddAppointmentDialog";
import { CalendarEventStatusField } from "../../fields/CalendarEventStatusField";
import { DateField } from "../../fields/DateField";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";
import { useCurrentDrivingSchoolId } from "../../hooks/useCurrentDrivingSchoolId";
import { useDialog } from "../../hooks/useDialog";
import { useEventDialog } from "../../hooks/useEventDialog";
import { isTheoryExam } from "../../model/autovioCalendarEvents";
import { AddressDisplay } from "../../misc/AddressDisplay";
import { Column } from "../../misc/Column";
import { EmptyState } from "../../misc/EmptyState";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";
import type { Instructor } from "../../providers/instructorsProvider";
import type { Student } from "../../providers/studentsProvider";
import { getRecentExamPeriodChoices, type Exam, type InstructorBranchIdsByInstructorId } from "../../utils/examFilters";
import { grants } from "../../backoffice.access_control";
import { ExamMobileListItem } from "./ExamMobileListItem";

const examTypeChoices = [
  { id: "TheoryExam", name: "Theoretische Prüfungen" },
  { id: "PracticalExam", name: "Praktische Prüfungen" },
];

export function DrivingSchoolExamsList() {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const drivingSchool = useCurrentDrivingSchool();
  const isCompact = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const examPeriodChoices = useMemo(() => getRecentExamPeriodChoices(DateTime.now()), []);
  const { data: instructors = [] } = useGetList<Instructor>(
    "instructors",
    {
      filter: { drivingSchoolId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );
  const instructorsById = useMemo(
    () => Object.fromEntries(instructors.map((instructor) => [instructor.id, instructor])),
    [instructors],
  );
  const instructorNamesById = useMemo(
    () => Object.fromEntries(instructors.map((instructor) => [instructor.id, instructor.name])),
    [instructors],
  );
  const instructorBranchIdsByInstructorId = useMemo<InstructorBranchIdsByInstructorId>(
    () => Object.fromEntries(instructors.map((instructor) => [instructor.id, instructor.assignedBranches ?? []])),
    [instructors],
  );
  const { dialogProps, openDialog } = useDialog();
  const { eventDialog, openEventDialog } = useEventDialog({ syncWithUrl: true });
  const canCreateExam = grants.has("Exam:create");
  const hasBranchFilter = (drivingSchool?.branches.length ?? 0) > 1;

  return (
    <ResponsiveListSurface variant="compactTabContent" data-testid="DrivingSchoolExamsList">
      <List
        title=" "
        resource="calendarEvents"
        filter={{ drivingSchoolId, allDates: true, type: ["TheoryExam", "PracticalExam"] }}
        storeKey={`DrivingSchoolExamsListV3@${drivingSchoolId}`}
        disableSyncWithLocation
        filters={[
          <SearchInput key="q" source="q" alwaysOn />,
          <SelectInput
            key="examPeriod"
            source="examPeriod"
            label="Zeitraum"
            choices={examPeriodChoices}
            emptyText="Gesamter Zeitraum"
            alwaysOn
          />,
          <SelectInput
            key="examType"
            source="examType"
            label="Prüfungsart"
            choices={examTypeChoices}
            emptyText="Alle Prüfungen"
            alwaysOn
          />,
          ...(hasBranchFilter
            ? [<_BranchFilter key="examBranchId" drivingSchoolId={drivingSchoolId} source="examBranchId" alwaysOn />]
            : []),
        ]}
        queryOptions={{ meta: { instructorBranchIdsByInstructorId, instructorNamesById } }}
        actions={canCreateExam ? <_CreateExamButton isCompact={isCompact} openDialog={openDialog} /> : false}
        sort={/* initial sort order: */ { field: "start", order: "DESC" }}
        perPage={25}
        exporter={false}
        empty={<EmptyState noEntriesText="Keine Prüfungen hinterlegt." />}
        sx={{
          pt: "20px",
          boxShadow: "none",
          "& .RaList-main": { overflow: "visible" },
          "& .RaList-main > .RaList-actions": { width: "100% !important" },
          "& .RaList-content": { boxShadow: "none", border: "none", borderRadius: 0, overflowX: "auto" },
          "& .RaDatagrid-root": { boxShadow: "none !important", borderRadius: 0 },
        }}
      >
        <_Datagrid instructorsById={instructorsById} rowClick={(_, __, record) => openEventDialog(record as Exam)} />
      </List>
      <AddAppointmentDialog {...dialogProps} tabs={["exam"]} />
      {eventDialog}
    </ResponsiveListSurface>
  );
}

function _CreateExamButton(props: { isCompact: boolean; openDialog: () => void }) {
  const { isCompact, openDialog } = props;
  return isCompact ? (
    <IconButton
      size="small"
      onClick={openDialog}
      aria-label="Prüfungstermin hinzufügen"
      sx={{ ml: "auto !important", mr: 0.5 }}
    >
      <AddIcon />
    </IconButton>
  ) : (
    <Button variant="outlined" startIcon={<AddIcon />} onClick={openDialog} size="small" sx={{ ml: "auto !important" }}>
      {"Hinzufügen\u00A0"}
    </Button>
  );
}

function _BranchFilter(props: { drivingSchoolId: string; source: string; alwaysOn?: boolean }) {
  const { drivingSchoolId, source } = props;
  return (
    <ReferenceInput
      source={source}
      reference="branches"
      filter={{ drivingSchoolId, isActive: true }}
      perPage={1000}
      alwaysOn
    >
      <SelectInput
        label="Filiale"
        emptyText="Alle Filialen"
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        size="small"
      />
    </ReferenceInput>
  );
}

function _Datagrid(props: { instructorsById: Record<string, Instructor>; rowClick: RowClickFunction }) {
  const { instructorsById, rowClick } = props;
  const { isLoading, data, total } = useListContext<Exam>();
  const drivingSchool = useCurrentDrivingSchool();
  const isCompact = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const studentIds = useMemo(() => [...new Set((data ?? []).map((exam) => exam.student.uid))], [data]);
  const { data: students = [] } = useGetMany<Student>(
    "students",
    { ids: studentIds },
    { enabled: studentIds.length > 0 },
  );
  const studentsById = useMemo(() => Object.fromEntries(students.map((student) => [student.id, student])), [students]);
  const empty = (
    <EmptyState
      noEntriesText="Keine Prüfungen hinterlegt."
      noMatchingEntriesText="Keine passenden Prüfungen gefunden."
    />
  );

  if (isLoading) {
    return <LoadingIndicator sx={{ position: "relative", borderRadius: "8px" }} />;
  }

  if (isCompact) {
    if (!data || data.length === 0 || total === 0) {
      return empty;
    }
    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data.map((exam) => (
          <RecordContextProvider key={exam.id} value={exam}>
            <ExamMobileListItem
              exam={exam}
              studentName={studentsById[exam.student.uid]?.name}
              instructorName={exam.instructorId ? instructorsById[exam.instructorId]?.name : undefined}
              branchNames={_getBranchNames(exam, instructorsById, drivingSchool?.branchesById ?? {})}
              status={<CalendarEventStatusField tooltip={false} variant="body2" />}
              onClick={() => {
                void rowClick(exam.id, "calendarEvents", exam);
              }}
            />
          </RecordContextProvider>
        ))}
      </Column>
    );
  }

  return (
    <Datagrid rowClick={rowClick} bulkActionButtons={false} empty={empty}>
      <FunctionField<Exam> label="Prüfungsart" sortable={false} render={_renderExamType} />
      <DateField label="Datum" source="start" showTime />
      <ReferenceField label="Fahrschüler" reference="students" source="student.uid" sortable={false} />
      <ReferenceField label="Fahrlehrer" reference="instructors" source="instructorId" sortable={false} emptyText="–" />
      <FunctionField<Exam>
        label="Filiale"
        sortable={false}
        render={(exam) => _getBranchNames(exam, instructorsById, drivingSchool?.branchesById ?? {}) || "–"}
      />
      <FunctionField<Exam>
        label="Ort"
        sortable={false}
        render={(exam) => <AddressDisplay address={exam.startLocation} oneLine />}
      />
      <CalendarEventStatusField label="Status" />
    </Datagrid>
  );
}

function _renderExamType(exam: Exam) {
  return <Typography variant="body2">{isTheoryExam(exam) ? "Theoretische Prüfung" : "Praktische Prüfung"}</Typography>;
}

function _getBranchNames(
  exam: Exam,
  instructorsById: Record<string, Instructor>,
  branchesById: Record<string, { name: string }>,
): string {
  const branchIds = isTheoryExam(exam)
    ? exam.branchId
      ? [exam.branchId]
      : []
    : (instructorsById[exam.instructorId ?? ""]?.assignedBranches ?? []);
  return branchIds
    .map((branchId) => branchesById[branchId]?.name)
    .filter(Boolean)
    .join(", ");
}
